import UiUtils from "@/assets/js/ui_utils.js";

function toNumber(value) {
  const numberValue = parseFloat(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

export function clampPercent(value) {
  const percent = toNumber(value);
  if (0 > percent) {
    return 0;
  }
  if (100 < percent) {
    return 100;
  }
  return percent;
}

export function getTotalAssetsValue({ wallet, currency }) {
  return UiUtils.getTotalMoney(wallet, currency);
}

export function getTotalEquitiesValue({ stocks, indexedStocks, wallet, currency }) {
  return UiUtils.getTotalEquitiesAsMoney(stocks, indexedStocks, wallet, currency);
}

export function getPositionGroupKey(stockTypeId) {
  switch (parseInt(stockTypeId)) {
    case 1:
    case 4:
      return "stocksAndEtfs";
    case 2:
    case 3:
      return "bonds";
    case 5:
      return "reits";
    default:
      return "other";
  }
}

export function getStocksByPositionGroup(wallet, groupKey) {
  if (!wallet?.s?.length) {
    return [];
  }

  return wallet.s.filter(position => getPositionGroupKey(position.stid) === groupKey);
}

export function getPositionGroupSummaries({ stocks, indexedStocks, wallet, currency, totalBalance }) {
  const groups = {
    stocksAndEtfs: { key: "stocksAndEtfs", avgValue: 0, positionsCount: 0, value: 0 },
    bonds: { key: "bonds", avgValue: 0, positionsCount: 0, value: 0 },
    reits: { key: "reits", avgValue: 0, positionsCount: 0, value: 0 },
    other: { key: "other", avgValue: 0, positionsCount: 0, value: 0 }
  };

  if (!wallet?.s?.length) {
    return Object.values(groups);
  }

  wallet.s.forEach(position => {
    const groupKey = getPositionGroupKey(position.stid);
    groups[groupKey].positionsCount += 1;
    const positionWallet = {
      ...wallet,
      s: [position]
    };

    groups[groupKey].value += getTotalEquitiesValue({
      stocks,
      indexedStocks,
      wallet: positionWallet,
      currency
    });
    groups[groupKey].avgValue += UiUtils.extractExchangeEquitiesAverageValue(
      stocks,
      indexedStocks,
      position.ex,
      currency,
      positionWallet
    );
  });

  return Object.values(groups).map(group => ({
    ...group,
    percent: 0 === toNumber(totalBalance)
      ? 0
      : group.value / toNumber(totalBalance) * 100
  }));
}

export function getFreeFunds({ cash, buySettlement, sellSettlement }) {
  return toNumber(cash) + toNumber(buySettlement) + toNumber(sellSettlement);
}

export function getGuaranteeDeposit({ wallet, stocks, indexedStocks, quotes, currency }) {
  if (!wallet) {
    return 0;
  }

  return UiUtils.getGuaranteeDeposit(wallet, currency, stocks, indexedStocks, quotes);
}

export function getTotalBuySettlementAmount({ wallet, currency }) {
  if (!wallet) {
    return 0;
  }

  return UiUtils.getTotalEquitiesInBuySettlementAsMoney(wallet, currency);
}

export function getTotalSellSettlementAmount({ wallet, currency }) {
  if (!wallet) {
    return 0;
  }

  return UiUtils.getTotalEquitiesInSellSettlementAsMoney(wallet, currency);
}

export function getCurrencySettlementAmounts({ wallet, currency }) {
  const settlementAmounts = {
    buy: 0,
    sell: 0
  };

  if (!wallet || !currency) {
    return settlementAmounts;
  }

  const normalizedCurrency = currency.toString().toUpperCase();
  wallet.s?.forEach(item => {
    if (normalizedCurrency !== item.cc?.toString().toUpperCase()) {
      return;
    }

    settlementAmounts.buy += toNumber(item.sttl_buy);
    settlementAmounts.sell += Math.abs(toNumber(item.sttl_sell));
  });

  return settlementAmounts;
}

export function getExcessFunds({ freeFunds, guaranteeDeposit }) {
  const excessFunds = toNumber(freeFunds) - toNumber(guaranteeDeposit);
  return 0 > excessFunds ? 0 : excessFunds;
}

export function getAvailableToWithdraw({ cash, buySettlement, sellSettlement, guaranteeDeposit }) {
  const inSettlementNet = toNumber(buySettlement) + toNumber(sellSettlement);
  const available = 0 <= inSettlementNet
    ? toNumber(cash) - toNumber(guaranteeDeposit)
    : toNumber(cash) + toNumber(buySettlement) - toNumber(guaranteeDeposit);

  return 0 > available ? 0 : available;
}

export function getPortfolioSummary({ wallet, stocks, indexedStocks, quotes, currency }) {
  const totalAssetsValue = getTotalAssetsValue({ wallet, currency });
  const totalEquitiesValue = getTotalEquitiesValue({ stocks, indexedStocks, wallet, currency });
  const totalBuySettlementAmount = getTotalBuySettlementAmount({ wallet, currency });
  const totalSellSettlementAmount = getTotalSellSettlementAmount({ wallet, currency });
  const freeFunds = getFreeFunds({
    cash: totalAssetsValue,
    buySettlement: totalBuySettlementAmount,
    sellSettlement: totalSellSettlementAmount
  });
  const totalBalance = toNumber(totalEquitiesValue) + toNumber(freeFunds);
  const guaranteeDeposit = getGuaranteeDeposit({ wallet, stocks, indexedStocks, quotes, currency });
  const excessFunds = getExcessFunds({ freeFunds, guaranteeDeposit });
  const availableToWithdraw = getAvailableToWithdraw({
    cash: totalAssetsValue,
    buySettlement: totalBuySettlementAmount,
    sellSettlement: totalSellSettlementAmount,
    guaranteeDeposit
  });

  return {
    availableToWithdraw,
    equitiesPercentage: 0 === totalBalance
      ? 0
      : toNumber(totalEquitiesValue) / totalBalance * 100,
    excessFunds,
    freeFunds,
    guaranteeDeposit,
    moneyPercentage: 0 === totalBalance
      ? 0
      : toNumber(totalAssetsValue) / totalBalance * 100,
    totalAssetsValue,
    totalBalance,
    totalBuySettlementAmount,
    totalSellSettlementAmount,
    totalEquitiesValue,
    positionGroupSummaries: getPositionGroupSummaries({
      stocks,
      indexedStocks,
      wallet,
      currency,
      totalBalance
    })
  };
}
